// Mock data for Dr. Carlos Castellon Medical Practice

export const doctorInfo = {
  name: "Dr. Carlos Castellon",
  title: "M.D.",
  tagline: "Ibiza's Concierge Physician | American-Born | Native English Speaker",
  quote: "Healing is an art. Medicine is a science. Health is a choice.",
  mainImage: "/images/hero.jpg",
  phone: "+34 635 794 368",
  email: "info@ibizasdrcarlos.com",
  about: `Dr. Carlos Castellon M.D. is the premier private physician in Ibiza for those who value expertise, discretion, and deeply personalized medical care. With a practice rooted in general medicine, pediatrics, preventative care, and wellness-based medicine, Dr. Castellon offers a rare blend of clinical precision and genuine compassion.\n\nHis clients include international families, high-profile travelers, and longtime island residents — all of whom trust him for his calm presence, medical insight, and unwavering professionalism.\n\nWhat truly sets him apart? Dr. Castellon is the only American-born physician practicing in Ibiza — and likely the entire Balearic Islands. With US clinical training and international medical education, he delivers seamless communication and culturally aligned care that international patients deeply value and often cannot find elsewhere on the island.\n\nSince 2016, Dr. Castellon has built a reputation as the trusted doctor for discerning patients seeking world-class medical support — whether at home, in luxury accommodations, or via secure telemedicine. His concierge-style practice prioritizes privacy, immediacy, and a human touch that goes far beyond the standard clinic experience.`,
  location: "Ibiza, Spain"
};

export const services = {
  comprehensiveCare: {
    title: "Comprehensive Medical Care",
    items: [
      {
        name: "General Medicine",
        description: "Personalized medical care for adults, including acute illness, chronic condition management, preventative health, and clinical decision-making grounded in evidence-based medicine."
      },
      {
        name: "Family Medicine",
        description: "Ongoing primary care for individuals and families, focused on continuity, prevention, and long-term health planning."
      },
      {
        name: "Pediatrics",
        description: "Medical care for infants, children, and adolescents, including acute illness, developmental concerns, preventative guidance, and family-centered care."
      }
    ]
  },
  telemedicine: {
    title: "Telemedicine & Private Consultations",
    items: [
      {
        name: "Telemedicine Consultations",
        description: "Secure remote consultations for assessment, guidance, follow-up care, and prescription management when clinically appropriate.",
        idealFor: ["Busy professionals", "International patients", "Follow-up and continuity of care"]
      },
      {
        name: "Second Opinions & Medical Interpretation",
        description: "Independent medical review and interpretation of diagnoses, laboratory results, imaging reports, and treatment plans. Explained clearly and pragmatically, without medical jargon."
      }
    ]
  },
  specializedTreatments: {
    title: "Specialized Treatments",
    items: [
      {
        name: "NAD Therapy",
        description: "NAD+ (Nicotinamide Adenine Dinucleotide) therapy to support cellular energy, cognitive function, and overall vitality. Administered under medical supervision."
      },
      {
        name: "Testosterone Therapy",
        description: "Hormone optimization for patients with clinically low testosterone levels. Includes comprehensive lab work, ongoing monitoring, and personalized treatment plans."
      },
      {
        name: "Medical Weight Loss",
        description: "Evidence-based weight management programs including GLP-1 medications when clinically appropriate. Combines medical oversight with lifestyle guidance for sustainable results."
      }
    ]
  },
  preventative: {
    title: "Preventative & Educational Medicine",
    items: [
      {
        name: "Preventative Medicine",
        description: "Focused on identifying risk early and reducing long-term disease through clinical risk assessment, lifestyle and behavioral guidance, and preventative strategies tailored to the individual."
      },
      {
        name: "Health Education & Optimization",
        description: "Evidence-based guidance on sleep and recovery, nutrition and metabolism, stress and nervous system regulation, and hormonal balance.",
        note: "No trends. No unregulated therapies. No shortcuts."
      }
    ]
  },
  laboratory: {
    title: "Laboratory Coordination",
    description: "Laboratory testing may be coordinated on a limited, case-by-case basis only when clinically indicated.",
    notes: [
      "Labs are not offered routinely or as a stand-alone service",
      "Billing includes physician time and coordination",
      "External laboratory fees are billed separately",
      "Availability depends on clinical necessity and logistics"
    ]
  },
  advisory: {
    title: "Special & Advisory Consultations",
    items: [
      {
        name: "Complex Case Advisory",
        description: "Medical consultation for patients with multi-system or unclear diagnoses, fragmented care across providers or countries, and complex medication or treatment histories."
      },
      {
        name: "International & Expat Care",
        description: "Experienced in caring for international patients, expatriates, and traveling families who require clear communication, medical documentation, and coordination across healthcare systems."
      }
    ]
  }
};

export const practiceModel = {
  title: "Concierge-Style Medicine",
  description: "A private, appointment-based medical practice offering home visits, telemedicine, and discreet, personalized care.",
  tagline: "Designed for patients who value time, clarity, and continuity."
};

export const practiceNotes = [
  "All care is provided in accordance with Spanish medical law",
  "Services are physician-led, evidence-based, and clinically indicated",
  "The physician reserves the right to decline patients or treatments at his discretion",
  "Booking a consultation does not guarantee any specific treatment or prescription"
];

export const legalDisclaimer = {
  title: "Legal & Medical Disclaimer",
  content: `The information provided on this website is for general informational purposes only and does not constitute medical advice, diagnosis, or treatment.\n\nAll medical services are provided exclusively through direct consultation with Dr. Carlos Castellon, M.D., either in person or via telemedicine, in accordance with Spanish medical law and professional standards.\n\nNO PHYSICIAN-PATIENT RELATIONSHIP\nUse of this website, submission of contact forms, or electronic communication does not establish a physician–patient relationship. A formal physician–patient relationship is established only after a confirmed appointment, appropriate medical evaluation, and mutual agreement to proceed with care.\n\nRIGHT OF REFUSAL\nDr. Carlos Castellon, M.D. reserves the right to decline to accept any individual as a patient, refuse to provide specific treatments or prescriptions, and discontinue the physician-patient relationship at his discretion. This includes but is not limited to: controlled substances, narcotics, sedatives, and any medication or treatment deemed clinically inappropriate.\n\nNO GUARANTEES\nMedical advice, prescriptions, diagnostic testing, and treatment decisions are provided solely at the discretion of the physician, based on clinical judgment, medical necessity, and applicable regulations. No guarantees are made regarding specific medical outcomes, availability of treatments, or issuance of prescriptions or medical certificates.\n\nSCOPE OF PRACTICE\nThis practice does not offer or promote illegal, non-regulated, or non-approved medical services. Certain services may be limited by jurisdiction, clinical indication, availability, or legal requirements within Spain. IV vitamin or infusion services are not offered.\n\nLABORATORY SERVICES\nLaboratory testing, when coordinated, is offered on a limited, case-by-case basis and is not provided as a routine or stand-alone service. External laboratory fees are billed separately from physician services.\n\nINDIVIDUAL RESULTS\nResults, outcomes, and responses to medical care may vary between individuals. No guarantees are made regarding specific medical results.\n\nEMERGENCIES\nThis is not an emergency medical service. For medical emergencies, patients should contact local emergency services (112 in Spain) immediately.\n\nGOVERNING LAW\nAll services are provided in accordance with Spanish medical law, the Spanish Medical Deontology Code, and applicable EU regulations including GDPR.`
};

export const telemedicineConsent = {
  title: "Telemedicine Consent & Acknowledgment",
  intro: "Telemedicine involves the provision of medical consultation and guidance through secure electronic communication technologies.",
  acknowledgments: [
    "Telemedicine is an alternative to in-person medical care and may have limitations, including the absence of physical examination or immediate diagnostic testing.",
    "Medical advice and treatment decisions are based on the information you provide, and accuracy of that information is essential.",
    "Not all medical conditions are appropriate for telemedicine. The physician may determine that in-person evaluation or referral is required.",
    "Prescriptions, medical certificates, or treatment recommendations are provided solely at the physician's discretion, in accordance with Spanish law.",
    "Telemedicine is not suitable for medical emergencies. In case of urgent or life-threatening symptoms, you must contact local emergency services immediately.",
    "Reasonable efforts are made to ensure privacy and data security; however, electronic communication may carry inherent risks beyond the physician's control."
  ],
  confirmations: [
    "You understand the nature and limitations of telemedicine",
    "You consent to receive medical consultation via electronic means",
    "You have had the opportunity to ask questions regarding telemedicine care"
  ]
};

export const privacyPolicy = {
  title: "Privacy & Data Protection Policy",
  intro: "This practice is committed to protecting the privacy and confidentiality of personal and medical data in accordance with the EU General Data Protection Regulation (GDPR) and applicable Spanish law.",
  dataController: {
    name: "Dr. Carlos Castellon, M.D.",
    specialty: "General & Preventative Medicine",
    location: "Ibiza, Spain"
  },
  dataCollected: [
    "Identification and contact information",
    "Medical history and clinical information",
    "Appointment and consultation records",
    "Communication related to care"
  ],
  purposes: [
    "Medical consultation and care delivery",
    "Clinical documentation and continuity of care",
    "Administrative, legal, and billing purposes"
  ],
  rights: [
    "Access your personal data",
    "Request correction of inaccurate data",
    "Request deletion where legally permissible",
    "Restrict or object to certain data processing",
    "Request data portability"
  ]
};

export const navItems = [
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Telemedicine", href: "#telemedicine" },
  { label: "Testimonials", href: "#testimonials" },
  { label: "FAQ", href: "#faq" },
  { label: "Pricing", href: "#pricing" },
  { label: "Contact", href: "#contact" }
];

export const testimonials = [
  {
    id: 1,
    name: "Alessandro L.",
    text: "Dr. Carlos is an outstanding professional — knowledgeable, attentive, and always available. He provided excellent care, explaining everything clearly and with great patience. His empathy and dedication to patients truly make a difference. I highly recommend him to anyone looking for a skilled and trustworthy doctor!",
    type: "Home Visit",
    rating: 5
  },
  {
    id: 2,
    name: "Kawtar",
    text: "Thank you Dr. Carlos and his team for taking the time to listen, explain everything clearly. It's rare to find a doctor who combines such kindness with skill, and I feel fortunate to have been under your care. Highly recommended.",
    type: "Telemedicine",
    rating: 5
  },
  {
    id: 3,
    name: "Ossie O.",
    text: "Just the best, not one but every time. Can't find better in Ibiza.",
    type: "Home Visit",
    rating: 5
  },
  {
    id: 4,
    name: "Kat R.",
    text: "Very professional and kind Doctor. He explained everything very well and found immediately the right treatment for my problem. I will use the service definitely again.",
    type: "Home Visit",
    rating: 5
  },
  {
    id: 5,
    name: "Mr. Evans",
    text: "Doctor Carlos and his staff are incredible and the personalized care and attention to detail is above and beyond. I always feel in very capable and professional hands.",
    type: "Telemedicine",
    rating: 5
  },
  {
    id: 6,
    name: "GH",
    text: "Carlos was very knowledgeable about a range of treatment options and quickly guided me to the appropriate solutions. He was also very accommodating and punctual.",
    type: "Home Visit",
    rating: 5
  },
  {
    id: 7,
    name: "Javi T.",
    text: "The best doctor on the island. Thanks Carlos for your professionalism and empathy.",
    type: "Telemedicine",
    rating: 5
  },
  {
    id: 8,
    name: "Ben",
    text: "Great service. Very helpful. Explained everything clearly.",
    type: "Telemedicine",
    rating: 5
  },
  {
    id: 9,
    name: "Penelope A.",
    text: "Dr. Castellón contacted me immediately. He was very caring and concerned, and gave me very clear, specific and detailed advice. His expert guidance was reassuring. I will use his services in the future.",
    type: "Telemedicine",
    rating: 5
  },
  {
    id: 10,
    name: "K.P.",
    text: "Dr. Castellón was incredibly compassionate, informative and attentive. He never made me feel rushed, explained everything in a very kind and simple way and was very efficient. I highly recommend him.",
    type: "Telemedicine",
    rating: 5
  },
  {
    id: 11,
    name: "International Patient",
    text: "As an expat in Ibiza, finding a doctor who speaks fluent English and understands international healthcare standards was a relief. Dr. Castellon exceeded all expectations.",
    type: "Home Visit",
    rating: 5
  },
  {
    id: 12,
    name: "Sarah M.",
    text: "Professional, punctual, and genuinely caring. Dr. Castellon made a stressful health situation much easier to navigate. Highly recommend for anyone seeking quality medical care in Ibiza.",
    type: "Home Visit",
    rating: 5
  }
];

export const singleVisitPricing = [
  {
    name: "Private Consultation",
    subtitle: "Home Visit",
    price: "€300",
    description: "Comprehensive in-person consultation at your residence, hotel, villa, or yacht",
    features: [
      "Full medical assessment",
      "Same-day or next-day availability",
      "Prescriptions when appropriate",
      "Covers entire island of Ibiza"
    ],
    popular: true
  },
  {
    name: "Telemedicine",
    subtitle: "Phone & Video Consultation",
    price: "€150 / €200",
    priceNote: "Established €150 · New Patient €200",
    description: "Secure consultation from anywhere in the world",
    features: [
      "Medical assessment & guidance",
      "Prescription refills & medication management",
      "Health status review",
      "Follow-up care",
      "Ideal for established and international patients"
    ],
    popular: false
  }
];

export const pricing = [
  {
    name: "Essential",
    subtitle: "Individual Only",
    description: "Core telemedicine access with responsive WhatsApp support",
    features: [
      "Up to 2 telemedicine consultations per month",
      "Same-day response via WhatsApp during working hours",
      "Prescription management (all refills conducted as clinical consultations)",
      "Basic health guidance and triage",
      "Access to specialist referral network",
      "Individual only",
      "Seasonal 4-month rate available on request"
    ],
    popular: false
  },
  {
    name: "Concierge",
    subtitle: "Individual | Couple | Family",
    description: "Comprehensive care with in-person visits and preventative health",
    features: [
      "Up to 8 telemedicine consultations per month",
      "Up to 6 home visits per month",
      "Priority WhatsApp access with same-day response during working hours",
      "Prescription management (all refills conducted as clinical consultations)",
      "Preventative health review and baseline labs coordination",
      "Specialist referral and care coordination",
      "Travel health advisory",
      "Seasonal 4-month rate available on request"
    ],
    popular: true
  },
  {
    name: "Private Physician",
    subtitle: "Individual | Couple | Family",
    description: "Full-service private physician with longevity and concierge support",
    features: [
      "Unlimited telemedicine consultations per month",
      "Up to 12 in-home or in-clinic visits per month",
      "Private phone number for direct access to the doctor",
      "Priority same-day response via WhatsApp during working hours",
      "Prescription management (all refills conducted as clinical consultations)",
      "Comprehensive preventative medicine and longevity protocol",
      "Hormone optimization and metabolic health monitoring",
      "Full labs coordination and results review",
      "Specialist referral and care coordination",
      "Concierge logistics support (hospital accompaniment, pharmacy liaison)",
      "Quarterly health strategy review",
      "Seasonal 4-month rate available on request"
    ],
    popular: false
  }
];

export const faq = [
  {
    question: "What areas do you cover in Ibiza?",
    answer: "I provide home visits across the entire island of Ibiza, including all towns, hotels, villas, and yachts."
  },
  {
    question: "Do you accept insurance?",
    answer: "Yes, however payment is required upfront at the time of service. I provide all necessary documentation for you to submit a claim to your insurance company for potential reimbursement."
  },
  {
    question: "What are your hours?",
    answer: "Standard hours are 10:00 AM to 6:00 PM. Appointments outside these hours are available by special arrangement."
  },
  {
    question: "How quickly can you visit?",
    answer: "Same-day and next-day appointments are often available depending on schedule and location. For urgent matters, I prioritize accordingly."
  },
  {
    question: "What payment methods do you accept?",
    answer: "I accept cash, credit card, and bank transfers. Payment is due at the time of service."
  },
  {
    question: "How do prescription refills work?",
    answer: "Prescription refills are not offered as a standalone service. All refills require a clinical consultation (€200 telemedicine appointment) so that your health status, medications, and any changes can be properly reviewed by the physician. This ensures safe, responsible prescribing in accordance with Spanish medical law."
  },
  {
    question: "Do you prescribe controlled substances?",
    answer: "Controlled substances, narcotics, and sedatives are not prescribed via telemedicine. All prescription refills require a clinical consultation and are billed at €200. All prescriptions are at the physician's discretion and in accordance with Spanish medical law."
  },
  {
    question: "Can you see children?",
    answer: "Yes, I provide pediatric care for infants, children, and adolescents, including acute illness, developmental concerns, and family-centered care."
  }
];

export const partners = [
  {
    name: "REGEN Clinic",
    type: "Partner Network",
    url: "https://theregenclinic.com",
    logo: "/images/partner-1.jpeg",
    featured: true
  },
  {
    name: "White Clinic",
    type: "Partner Network",
    url: null,
    logo: "/images/partner-2.png",
    featured: true
  },
  {
    name: "Dr. Krystyna",
    type: "Partner Network",
    url: "https://drkrystyna.com",
    logo: "https://drkrystyna.com/wp-content/uploads/2020/04/cropped-line-stamp-logo-192x192.png",
    featured: true
  },
  {
    name: "Ranjith Chambers",
    type: "Osteopath",
    url: null,
    logo: "/images/partner-3.jpg",
    featured: true
  },
  {
    name: "NADdict",
    type: "Partner Network",
    url: "https://naddict.com",
    logo: "https://naddict.com/cdn/shop/t/29/assets/2025-06-Naddict-logo-black-1024x432.png?v=79878281161067807791765287638",
    featured: true
  },
  {
    name: "Policlínica Nuestra Señora del Rosario",
    type: "Hospital",
    url: "https://www.grupopoliclinica.es/centros/policlinica-ntra-senora-del-rosario/",
    logo: "https://www.grupopoliclinica.es/wp-content/uploads/LOGO-DEFINITIVO2.jpg",
    featured: false
  }
];
